/**
 * The skill store, as the pane sees it: the list and the folders, loaded
 * together, with the mutations phase 1 needs.
 *
 * Same shape as `useThreads`: a discriminated state, a `reload`, and a ref for
 * the token so a refresh does not refetch. The mutations update the list in
 * place the way the reference's `setQueryData` calls do — a toggle or a move
 * shows immediately and is rolled back if the agent refuses.
 *
 * An AutoCAD skill is listed once per year folder (`autocad:<uuid>@<year>`) but is
 * one skill on disk: a move, a toggle or a delete applies to every row of it, and
 * its grouping folders are vendor-wide (`kind: "autocad"`, no year).
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  createSkillsClient,
  type HostKind,
  type Skill,
  type SkillFolder,
  type SkillsClient,
} from "../lib/skills";

export type SkillsState =
  | { kind: "idle" }
  | { kind: "loading" }
  | { kind: "error"; message: string; status: number | null }
  | { kind: "loaded"; skills: Skill[]; folders: SkillFolder[] };

export interface SkillsView {
  state: SkillsState;
  /** The client for the detail view's own calls (versions, history, run). */
  client: SkillsClient | null;
  reload: () => void;
  /** Reload and resolve once the new list is in state — to reopen a row it changed. */
  refresh: () => Promise<void>;
  refreshing: boolean;
  createFolder: (name: string, version: string, kind?: HostKind) => Promise<SkillFolder>;
  move: (skillId: string, folderName: string | null) => Promise<void>;
  setEnabled: (skillId: string, enabled: boolean) => Promise<void>;
  remove: (skillId: string) => Promise<void>;
}

export function useSkills(orgId: string | null, token: string | null): SkillsView {
  const [state, setState] = useState<SkillsState>({ kind: "idle" });
  const [attempt, setAttempt] = useState(0);
  const [refreshing, setRefreshing] = useState(false);

  const tokenRef = useRef(token);
  tokenRef.current = token;

  // One client per (org, token) — headers are baked in at creation.
  const client = useMemo(
    () => (orgId && token ? createSkillsClient({ token, orgId }) : null),
    [orgId, token],
  );
  const clientRef = useRef(client);
  clientRef.current = client;

  useEffect(() => {
    if (!orgId || !tokenRef.current) {
      setState({ kind: "idle" });
      return;
    }
    const c = createSkillsClient({ token: tokenRef.current, orgId });
    let cancelled = false;
    setState((prev) => (prev.kind === "loaded" ? prev : { kind: "loading" }));
    setRefreshing(true);

    Promise.all([c.list(), c.folders()])
      .then(([skills, folders]) => {
        if (!cancelled) setState({ kind: "loaded", skills, folders });
      })
      .catch((error: unknown) => {
        if (cancelled) return;
        const status = (error as { status?: number })?.status ?? null;
        setState({ kind: "error", message: error instanceof Error ? error.message : String(error), status });
      })
      .finally(() => {
        if (!cancelled) setRefreshing(false);
      });

    return () => {
      cancelled = true;
    };
  }, [orgId, attempt]);

  const reload = useCallback(() => setAttempt((n) => n + 1), []);

  const refresh = useCallback(async () => {
    const c = clientRef.current;
    if (!c) return;
    const [skills, folders] = await Promise.all([c.list(), c.folders()]);
    setState({ kind: "loaded", skills, folders });
  }, []);

  const patch = useCallback((skillId: string, change: Partial<Skill>) => {
    setState((prev) => {
      if (prev.kind !== "loaded") return prev;
      const skill = prev.skills.find((s) => s.id === skillId);
      return { ...prev, skills: prev.skills.map((s) => (skill && sameSkill(s, skill) ? { ...s, ...change } : s)) };
    });
  }, []);

  const need = (): SkillsClient => {
    if (!clientRef.current) throw new Error("not signed in");
    return clientRef.current;
  };

  const createFolder = useCallback(async (name: string, version: string, kind?: HostKind) => {
    const folder = await need().createFolder(name, version, kind);
    setState((prev) =>
      prev.kind === "loaded"
        ? { ...prev, folders: [...prev.folders, folder].sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: "base" })) }
        : prev,
    );
    return folder;
  }, []);

  const move = useCallback(async (skillId: string, folderName: string | null) => {
    await need().move(skillId, folderName);
    // The folder cards' counts are the agent's; keep them true without a
    // refetch by moving the one that moved.
    setState((prev) => {
      if (prev.kind !== "loaded") return prev;
      const skill = prev.skills.find((s) => s.id === skillId);
      if (!skill) return prev;
      const from = skill.parentFolder;
      return {
        ...prev,
        skills: prev.skills.map((s) => (sameSkill(s, skill) ? { ...s, parentFolder: folderName } : s)),
        folders: prev.folders.map((f) => {
          if (!folderHolds(f, skill)) return f;
          if (f.name === from) return { ...f, skillCount: Math.max(0, f.skillCount - 1) };
          if (f.name === folderName) return { ...f, skillCount: f.skillCount + 1 };
          return f;
        }),
      };
    });
  }, []);

  const setEnabled = useCallback(async (skillId: string, enabled: boolean) => {
    patch(skillId, { enabled });
    try {
      await need().setEnabled(skillId, enabled);
    } catch (error) {
      patch(skillId, { enabled: !enabled });
      throw error;
    }
  }, [patch]);

  const remove = useCallback(async (skillId: string) => {
    await need().remove(skillId);
    setState((prev) => {
      if (prev.kind !== "loaded") return prev;
      const skill = prev.skills.find((s) => s.id === skillId);
      return {
        ...prev,
        skills: prev.skills.filter((s) => !(skill ? sameSkill(s, skill) : s.id === skillId)),
        folders: skill?.parentFolder
          ? prev.folders.map((f) =>
              folderHolds(f, skill) && f.name === skill.parentFolder
                ? { ...f, skillCount: Math.max(0, f.skillCount - 1) }
                : f,
            )
          : prev.folders,
      };
    });
  }, []);

  return { state, client, reload, refresh, refreshing, createFolder, move, setEnabled, remove };
}

/** Two rows of one skill: the same row, or two year folders of one AutoCAD skill. */
function sameSkill(a: Skill, b: Skill): boolean {
  if (a.id === b.id) return true;
  return a.target.kind === "autocad" && b.target.kind === "autocad" && a.baseId === b.baseId;
}

/** Whether a folder is one a skill can sit in: its year's store, or the AutoCAD store. */
function folderHolds(folder: SkillFolder, skill: Skill): boolean {
  if (skill.target.kind === "autocad") return folder.kind === "autocad";
  return folder.kind !== "autocad" && folder.version === skill.target.version;
}
